/**
 * utils/sorMetadataExtractor.js
 * Extracteur de métadonnées pour fichiers .sor de Viavi - EXTRACTION BASÉE SUR LA SPEC SR-4731
 */

import logger from "./logger.js";

export class SorMetadataExtractor {
  /**
   * Extraire toutes les métadonnées d'un fichier .sor
   * @param {File} file - Fichier .sor à analyser
   * @returns {Promise<Object>} Métadonnées extraites
   */
  static async extractMetadata(file) {
    try {
      const buffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);

      // Extraire les sections selon la spec SR-4731
      const sections = this._extractSections(uint8Array);

      // Parser les métadonnées principales
      const metadata = {
        // Métadonnées de la spec SR-4731
        ...this._parseGenParams(sections.GenParams),
        ...this._parseFxdParams(sections.FxdParams),
        ...this._parseFSetupParams(sections.FSETup),
        ...this._parseSupParams(sections.SupParams),
        ...this._parseOTDSConfig(sections.ActernaConfig),
      };

      return {
        success: true,
        metadata: metadata,
        error: null,
      };
    } catch (error) {
      logger.error(
        {
          context: "metadata-extraction",
          fileName: file.name,
          fileSize: file.size,
          error: error.message,
          stack: error.stack,
        },
        `❌ Erreur extraction métadonnées ${file.name}`
      );
      return {
        success: false,
        metadata: null,
        error: error.message,
      };
    }
  }

  /**
   * Extraire les sections du fichier .sor selon la spec SR-4731
   */
  static _extractSections(uint8Array) {
    const sections = {};
    const decoder = new TextDecoder("utf-8");

    try {
      // Convertir en string pour rechercher les sections
      const content = decoder.decode(uint8Array);

      // Parser le bloc Map (premier bloc obligatoire)
      const mapData = this._parseMapBlock(uint8Array);
      sections.mapData = mapData;

      // Extraire chaque section selon le Map
      if (mapData && mapData.blocks) {
        let currentOffset = mapData.mapBlockSize;

        mapData.blocks.forEach((blockInfo) => {
          const blockName = blockInfo.name;
          const blockSize = blockInfo.size;

          // Extraire les données de ce bloc
          const blockData = uint8Array.slice(currentOffset, currentOffset + blockSize);
          sections[blockName] = {
            data: blockData,
            text: decoder.decode(blockData),
            size: blockSize,
            offset: currentOffset,
          };

          currentOffset += blockSize;
        });
      }

      // Extraire aussi les sections Viavi spécifiques
      this._extractViaviSections(content, sections);
    } catch (error) {
      logger.warn(
        {
          context: "section-decoding",
          error: error.message,
        },
        "Erreur décodage sections"
      );
    }

    return sections;
  }

  /**
   * Parser le bloc Map selon la spec SR-4731
   */
  static _parseMapBlock(uint8Array) {
    try {
      const dataView = new DataView(uint8Array.buffer);

      // Les 4 premiers bytes sont "Map\0"
      if (uint8Array[0] !== 0x4d || uint8Array[1] !== 0x61 || uint8Array[2] !== 0x70 || uint8Array[3] !== 0x00) {
        logger.warn(
          {
            context: "map-signature-validation",
            expectedSignature: "Map\\0",
            actualBytes: [uint8Array[0], uint8Array[1], uint8Array[2], uint8Array[3]],
          },
          "Signature Map non trouvée"
        );
        return null;
      }

      // Offset 4-5: version number (little endian)
      const version = dataView.getUint16(4, true);

      // Offset 6-9: taille du bloc Map (little endian)
      const mapBlockSize = dataView.getUint32(6, true);

      // Offset 10-11: nombre de blocs (little endian)
      const numBlocks = dataView.getUint16(10, true);

      // Parser les informations de chaque bloc
      const blocks = [];
      let offset = 12; // Après l'en-tête Map

      for (let i = 0; i < numBlocks && offset < mapBlockSize; i++) {
        // Lire le nom du bloc (string terminée par \0)
        let nameEnd = offset;
        while (nameEnd < uint8Array.length && uint8Array[nameEnd] !== 0) {
          nameEnd++;
        }

        const blockName = new TextDecoder().decode(uint8Array.slice(offset, nameEnd));
        offset = nameEnd + 1; // +1 pour le \0

        if (offset + 6 <= uint8Array.length) {
          // Version du bloc (2 bytes little endian)
          const blockVersion = dataView.getUint16(offset, true);
          offset += 2;

          // Taille du bloc (4 bytes little endian)
          const blockSize = dataView.getUint32(offset, true);
          offset += 4;

          blocks.push({
            name: blockName,
            version: blockVersion,
            size: blockSize,
          });
        }
      }

      return {
        version: version / 100,
        mapBlockSize: mapBlockSize,
        numBlocks: numBlocks,
        blocks: blocks,
      };
    } catch (error) {
      logger.warn(
        {
          context: "map-parsing",
          error: error.message,
        },
        "Erreur parsing Map"
      );
      return null;
    }
  }

  /**
   * Parser les paramètres généraux (GenParams) selon SR-4731
   */
  static _parseGenParams(section) {
    if (!section || !section.data) return {};

    const params = {};

    try {
      const data = section.data;
      const decoder = new TextDecoder("utf-8");
      let offset = 0;

      // Version 2.x commence par "GenParams\0"
      if (data[0] === 0x47 && data[1] === 0x65 && data[2] === 0x6e) {
        // "Gen"
        offset = 10; // Passer "GenParams\0"

        // Language (2 bytes) - extraire mais ne pas inclure
        const language = decoder.decode(data.slice(offset, offset + 2)); // eslint-disable-line no-unused-vars
        offset += 2;
      }

      // Parser les champs selon la spec SR-4731
      const fields = this._parseStringFields(data, offset, [
        "cableId",
        "fiberId", // ne pas inclure dans metadata
        "fiberType", // ne pas inclure dans metadata
        "wavelength", // 2 bytes uint - VOICI LA LONGUEUR D'ONDE ACTIVE !
        "locationA",
        "locationB",
        "cableCode",
        "buildCondition", // ne pas inclure dans metadata
        "unknown8bytes", // 8 bytes
        "operator",
        "comments",
      ]);

      // Filtrer les champs à exclure des métadonnées
      const excludedFields = ["fiberId", "fiberType", "buildCondition"];

      Object.keys(fields).forEach((key) => {
        if (!excludedFields.includes(key)) {
          params[key] = fields[key];
        }
      });

      // Extraire la longueur d'onde active (champ le plus important)
      if (fields.wavelength) {
        // La longueur d'onde est encodée × 10 (ex: 15500 pour 1550nm)
        const wavelengthValue = fields.wavelength;
        if (wavelengthValue > 1000) {
          //params.longueurOnde = Math.round(wavelengthValue / 10);
        } else {
          // Cas des anciens formats (facteur différent)
          ///params.longueurOnde = wavelengthValue;
        }
      }
    } catch (error) {
      logger.warn(
        {
          context: "genparams-parsing",
          error: error.message,
          sectionSize: section?.data?.length || 0,
        },
        "Erreur parsing GenParams"
      );
    }

    return params;
  }

  /**
   * Parser les paramètres fixes (FxdParams) selon SR-4731
   */
  static _parseFxdParams(section) {
    if (!section || !section.data) return {};

    const params = {};

    try {
      const data = section.data;
      const dataView = new DataView(data.buffer, data.byteOffset);
      let offset = 0;

      // Version 2.x commence par "FxdParams\0"
      if (data[0] === 0x46 && data[1] === 0x78 && data[2] === 0x64) {
        // "Fxd"
        offset = 10; // Passer "FxdParams\0"
      }

      // Structure selon SR-4731 pour version 2.x
      if (offset + 82 <= data.length) {
        // Date/time (4 bytes) - Unix timestamp
        const timestamp = dataView.getUint32(offset, true);
        params.dateTime = new Date(timestamp * 1000);
        offset += 4;

        // Unknown (2 bytes)
        offset += 2;

        // Wavelength (2 bytes) - CONFIRMATION DE LA LONGUEUR D'ONDE
        const wavelengthRaw = dataView.getUint16(offset, true);
        if (wavelengthRaw > 1000) {
          params.longueurOndeConfirmation = Math.round(wavelengthRaw / 10);
        } else {
          params.longueurOndeConfirmation = wavelengthRaw;
        }
        offset += 2;

        // Unknown (10 bytes)
        offset += 10;

        // Pulse width (2 bytes) - nanoseconds
        params.pulseWidth = dataView.getUint16(offset, true);
        offset += 2;

        // Distance spacing (4 bytes)
        const distanceSpacing = dataView.getUint32(offset, true); // eslint-disable-line no-unused-vars
        //params.distanceSpacing = distanceSpacing * 2e-6; // Conversion en mètres
        offset += 4;

        // Number of data points (4 bytes)
        //params.numberOfDataPoints = dataView.getUint32(offset, true);
        offset += 4;

        // Index of refraction (4 bytes)
        const iorRaw = dataView.getUint32(offset, true);
        // According to the specification we must divide by 10^6 but the the value from FiberCable Shows that we need only 10^5
        params.indexRefraction = iorRaw / 1e5;
        offset += 4;

        // Backscattering coefficient (2 bytes)
        const backscatterRaw = dataView.getUint16(offset, true);
        params.backscatteringCoeff = backscatterRaw * -0.1; // dB
        offset += 2;

        // Number of averages (4 bytes)
        //params.numberOfAverages = dataView.getUint32(offset, true);
        offset += 4;

        // Range (4 bytes)
        const rangeRaw = dataView.getUint32(offset, true); // eslint-disable-line no-unused-vars
        //params.range = rangeRaw * 1e-6; // km
        offset += 4;
      }
    } catch (error) {
      logger.warn(
        {
          context: "fxdparams-parsing",
          error: error.message,
          sectionSize: section?.data?.length || 0,
        },
        "Erreur parsing FxdParams"
      );
    }

    return params;
  }

  /**
   * Parser les champs string/int d'une section
   */
  static _parseStringFields(data, startOffset, fieldDefinitions) {
    const fields = {};
    const decoder = new TextDecoder("utf-8");
    const dataView = new DataView(data.buffer, data.byteOffset);
    let offset = startOffset;

    fieldDefinitions.forEach((fieldName) => {
      try {
        if (offset >= data.length) return;

        if (fieldName === "fiberType" || fieldName === "wavelength") {
          // Champ de 2 bytes unsigned integer
          if (offset + 2 <= data.length) {
            fields[fieldName] = dataView.getUint16(offset, true);
            offset += 2;
          }
        } else if (fieldName === "buildCondition") {
          // 2 caractères sans \0
          if (offset + 2 <= data.length) {
            fields[fieldName] = decoder.decode(data.slice(offset, offset + 2));
            offset += 2;
          }
        } else if (fieldName === "unknown8bytes") {
          // 8 bytes à ignorer
          offset += 8;
        } else {
          // String terminée par \0
          let stringEnd = offset;
          while (stringEnd < data.length && data[stringEnd] !== 0) {
            stringEnd++;
          }

          if (stringEnd > offset) {
            const value = decoder.decode(data.slice(offset, stringEnd));
            fields[fieldName] = value;
          }
          offset = stringEnd + 1; // +1 pour le \0
        }
      } catch (error) {
        logger.warn(
          {
            context: "field-parsing",
            fieldName,
            offset,
            error: error.message,
          },
          `Erreur parsing field ${fieldName}`
        );
      }
    });

    return fields;
  }

  /**
   * Extraire les sections Viavi spécifiques (pour compatibilité)
   */
  static _extractViaviSections(content, sections) {
    // FSETup (section de configuration Viavi)
    const fsetupIndex = content.indexOf("FSETup:FNAming");
    if (fsetupIndex !== -1) {
      const fsetupEnd = content.indexOf("ActernaMiniCurve", fsetupIndex);
      sections.FSETup = content.substring(fsetupIndex, fsetupEnd > 0 ? fsetupEnd : fsetupIndex + 3000);
    }

    // OTDS (configuration OTDR Viavi)
    const otdsIndex = content.indexOf("OTDS:EXPert");
    if (otdsIndex !== -1) {
      const otdsEnd = content.indexOf("FO:ROTDR", otdsIndex);
      sections.ActernaConfig = content.substring(otdsIndex, otdsEnd > 0 ? otdsEnd : otdsIndex + 5000);
    }
  }

  /**
   * Parser les paramètres FSETup (configurations Viavi)
   */
  static _parseFSetupParams(section) {
    if (!section) return {};

    const params = {};

    try {
      const patterns = {
        origine: /FSETup:ORIGin\s+"([^"]+)"/,
        direction: /FSETup:DIRection\s+(\w+)/,
        cableIdOrigine: /FSETup:CABleid\s+ORI,"([^"]+)"/,
        cableIdDestination: /FSETup:CABleid\s+END,"([^"]+)"/,
        fiberIdOrigine: /FSETup:FIBerid\s+ORI,"([^"]+)"/, // ne pas inclure
        fiberIdDestination: /FSETup:FIBerid\s+END,"([^"]+)"/, // ne pas inclure
        numeroFibreOrigine: /FSETup:FNUMber\s+ORI,(\d+)/,
        numeroFibreDestination: /FSETup:FNUMber\s+END,(\d+)/,
        operateur: /FSETup:OPERator\s+"([^"]+)"/,
        contracteur: /FSETup:CONTractor\s+"([^"]+)"/,
        jobId: /FSETup:JOBid\s+"([^"]+)"/,
        endPoint: /FSETup:END\s+"([^"]+)"/,
        testPoint: /FSETup:TPOint\s+"([^"]+)"/,
      };

      // Champs à extraire mais pas inclure dans les métadonnées
      const excludedFields = ["fiberIdOrigine", "fiberIdDestination"];

      Object.keys(patterns).forEach((key) => {
        const match = section.match(patterns[key]);
        if (match && match[1] !== undefined && match[1] !== "") {
          // Ne pas inclure les champs exclus dans les métadonnées finales
          if (!excludedFields.includes(key)) {
            if (key.includes("numeroFibre")) {
              params[key] = parseInt(match[1], 10);
            } else {
              params[key] = match[1];
            }
          }
        }
      });
    } catch (error) {
      logger.warn(
        {
          context: "fsetup-parsing",
          error: error.message,
        },
        "Erreur parsing FSETup"
      );
    }

    return params;
  }

  /**
   * Parser les paramètres de support (SupParams)
   */
  static _parseSupParams(section) {
    if (!section) return {};

    const params = {};

    try {
      const content = section.text || section;

      // Parse the null-separated string structure
      const parts = content.split("\u0000").filter((part) => part.length > 0);

      if (parts.length >= 6) {
        // Expected structure: [SupParams, Manufacturer, Model, PartNumber, SerialNumber, PartNumber, Version]
        params.equipement = `${parts[1]} ${parts[2]}`; // "Viavi SmartOTDR"
        params.modeleEquipement = parts[2]; // "SmartOTDR"
        params.module = parts[4]; // "E126B"
        params.versionLogiciel = parts[parts.length - 1]; // "24.2.5"
        params.serialNumber = parts[3]; // "43803"
      } else {
        // Fallback to original regex approach
        const equipmentMatch = content.match(/Viavi\s*(\w+)/);
        if (equipmentMatch) {
          params.equipement = equipmentMatch[0];
          params.modeleEquipement = equipmentMatch[1];
        }

        const serialMatch = content.match(/([A-Z0-9]{4,8})/);
        if (serialMatch) {
          params.module = serialMatch[1];
        }
      }
    } catch (error) {
      logger.warn(
        {
          context: "supparams-parsing",
          error: error.message,
        },
        "Erreur parsing SupParams"
      );
    }

    return params;
  }

  /**
   * Parser la configuration OTDS
   */
  static _parseOTDSConfig(section) {
    if (!section) return {};

    const params = {};

    try {
      const content = section.text || section;

      const patterns = {
        modeAcquisition: /OTDS:ACQ\s+(\w+)/, // ne pas inclure
        portUtilise: /OTDS:PIND\s+(\w+)/, // ne pas inclure
        uniteDistance: /OTDS:UNIT\s+(\w+)/,
        seuilConnecteur: /OTDS:ALAR:CONN\s+(\d+)/, // ne pas inclure
        seuilReflection: /OTDS:ALAR:REFL\s+(-?\d+)/, // ne pas inclure
        seuilEpissure: /OTDS:ALAR:SPLIC\s+(\d+)/, // ne pas inclure
        maxTime: /OTDS:MAXT\s+(\d+)/, // ne pas inclure
        displayLevel: /OTDS:DSPL\s+(\d+)/, // ne pas inclure
        //pulseWidth: /OTDS:UBT\s+([\d.]+)/,
        range: /OTDS:UBD\s+([\d.]+)/,
      };

      // Champs à extraire mais pas inclure dans les métadonnées
      const excludedFields = [
        "modeAcquisition",
        "portUtilise",
        "seuilConnecteur",
        "seuilReflection",
        "seuilEpissure",
        "maxTime",
        "displayLevel",
      ];

      Object.keys(patterns).forEach((key) => {
        const match = content.match(patterns[key]);
        if (match && match[1]) {
          // Ne pas inclure les champs exclus dans les métadonnées finales
          if (!excludedFields.includes(key)) {
            if (
              key.includes("seuil") ||
              key.includes("max") ||
              key.includes("display") ||
              key.includes("pulse") ||
              key.includes("range")
            ) {
              params[key] = parseFloat(match[1]);
            } else {
              params[key] = match[1];
            }
          }
        }
      });
    } catch (error) {
      logger.warn(
        {
          context: "otds-parsing",
          error: error.message,
        },
        "Erreur parsing OTDS Config"
      );
    }

    return params;
  }

  /**
   * Traitement par lot de fichiers
   */
  static async extractBatchMetadata(files) {
    const results = [];

    for (const file of files) {
      const result = await this.extractMetadata(file.file || file);
      results.push({
        fileName: file.name || file.fileName,
        ...result,
      });
    }

    return results;
  }

  /**
   * Enrichir un objet fichier existant avec les métadonnées
   */
  static async enrichFileObject(fileObject) {
    const extractionResult = await this.extractMetadata(fileObject.file);

    if (extractionResult.success) {
      // Enrichir l'objet existant avec toutes les métadonnées du fichier
      fileObject.metadata = extractionResult.metadata;

      // Garder la compatibilité avec l'ancien système
      if (!fileObject.parsed) {
        fileObject.parsed = {};
      }

      // Utiliser la longueur d'onde extraite (priorité à GenParams, puis FxdParams)
      const metadata = extractionResult.metadata;
      let wavelength = null;

      if (metadata.longueurOnde) {
        wavelength = metadata.longueurOnde;
      } else if (metadata.longueurOndeConfirmation) {
        wavelength = metadata.longueurOndeConfirmation;
      }

      if (wavelength) {
        fileObject.parsed.longueurOnde = wavelength;
      }

      // Ajouter l'index de réfraction
      if (metadata.indexRefraction) {
        fileObject.parsed.indexRefraction = metadata.indexRefraction;
      }
    } else {
      // En cas d'erreur, stocker l'erreur
      fileObject.metadata = {
        error: extractionResult.error,
      };
    }

    return fileObject;
  }
}
